import React from 'react';

/**
 * Renders markdown-style text with support for:
 * - **bold** text
 * - Bullet lists (- or *)
 * - Numbered lists
 * - Headers (##)
 * - Line breaks
 */
export const renderMarkdown = (text, colors, isMobile = false) => {
  if (!text) return null;
  
  // Split by double newlines to get paragraphs/blocks
  const blocks = text.split(/\n\n+/);
  const result = [];
  
  blocks.forEach((block, pIdx) => {
    block = block.trim();
    if (!block) return;
    
    // Check if it's a header (starts with ##)
    if (block.startsWith('##')) {
      const headerText = block.replace(/^##+\s*/, '');
      result.push(
        <div key={`header-wrapper-${pIdx}`}>
          <h3 
            key={`header-${pIdx}`}
            style={{
              fontSize: isMobile ? '1.4rem' : '1.75rem',
              fontWeight: '700',
              color: colors.text,
              marginTop: '2.5rem',
              marginBottom: '1rem',
              lineHeight: '1.3',
              letterSpacing: '-0.01em'
            }}
          >
            {headerText}
          </h3>
          <div style={{
            width: '100%',
            height: '1px',
            backgroundColor: '#ffffff',
            opacity: 0.2,
            marginBottom: '1.5rem'
          }} />
        </div>
      );
      return;
    }
    
    // Check if it's a standalone bold text header (like **Header Text**)
    const boldHeaderMatch = block.match(/^\*\*(.+?)\*\*$/);
    if (boldHeaderMatch && block.split('\n').length === 1) {
      const headerText = boldHeaderMatch[1];
      result.push(
        <div key={`header-wrapper-${pIdx}`}>
          <h3 
            key={`header-${pIdx}`}
            style={{
              fontSize: isMobile ? '1.4rem' : '1.75rem',
              fontWeight: '700',
              color: colors.text,
              marginTop: '2.5rem',
              marginBottom: '1rem',
              lineHeight: '1.3',
              letterSpacing: '-0.01em'
            }}
          >
            {headerText}
          </h3>
          <div style={{
            width: '100%',
            height: '1px',
            backgroundColor: '#ffffff',
            opacity: 0.2,
            marginBottom: '1.5rem'
          }} />
        </div>
      );
      return;
    }
    
    // Check if block contains a list (lines starting with - or * or number)
    const lines = block.split(/\n/).filter(line => line.trim());
    const listStartIndex = lines.findIndex(line => 
      line.match(/^[-*]\s/) || line.match(/^\d+\.\s/)
    );
    
    // If we found a list, split into paragraph part and list part
    if (listStartIndex > 0) {
      // Render paragraph before list
      const paraText = lines.slice(0, listStartIndex).join(' ').trim();
      if (paraText) {
        result.push(
          <p 
            key={`para-${pIdx}-pre`}
            style={{
              lineHeight: '1.85',
              fontSize: isMobile ? '1rem' : '1.1rem',
              margin: '1.25rem 0',
              color: colors.text,
              maxWidth: '100%'
            }}
          >
            {renderInlineMarkdown(paraText, colors)}
          </p>
        );
      }
      
      // Render list
      const listItems = lines.slice(listStartIndex);
      result.push(
        <ul 
          key={`list-${pIdx}`}
          style={{
            margin: '1rem 0',
            paddingLeft: '1.5rem',
            listStyleType: 'disc',
            color: colors.text
          }}
        >
          {listItems.map((item, iIdx) => {
            if (!item.trim()) return null;
            const cleanItem = item.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').trim();
            if (!cleanItem) return null;
            return (
              <li 
                key={`item-${iIdx}`}
                style={{
                  marginBottom: '0.75rem',
                  lineHeight: '1.85',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  paddingLeft: '0.25rem'
                }}
              >
                {renderInlineMarkdown(cleanItem, colors)}
              </li>
            );
          }).filter(Boolean)}
        </ul>
      );
    } else if (listStartIndex === 0) {
      // Entire block is a list
      result.push(
        <ul 
          key={`list-${pIdx}`}
          style={{
            margin: '1rem 0',
            paddingLeft: '1.5rem',
            listStyleType: 'disc',
            color: colors.text
          }}
        >
          {lines.map((item, iIdx) => {
            if (!item.trim()) return null;
            const cleanItem = item.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').trim();
            if (!cleanItem) return null;
            return (
              <li 
                key={`item-${iIdx}`}
                style={{
                  marginBottom: '0.75rem',
                  lineHeight: '1.85',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  paddingLeft: '0.25rem'
                }}
              >
                {renderInlineMarkdown(cleanItem, colors)}
              </li>
            );
          }).filter(Boolean)}
        </ul>
      );
    } else {
      // Regular paragraph - handle single newlines as line breaks
      const hasSingleNewlines = block.includes('\n');
      if (hasSingleNewlines) {
        // Split by single newlines and render each line
        const paraLines = block.split(/\n/).filter(line => line.trim());
        paraLines.forEach((line, lIdx) => {
          result.push(
            <p
              key={`line-${pIdx}-${lIdx}`}
              style={{
                lineHeight: '1.85',
                fontSize: isMobile ? '1rem' : '1.1rem',
                margin: lIdx === 0 ? '1.25rem 0 0.75rem 0' : '0.75rem 0',
                color: colors.text
              }}
            >
              {renderInlineMarkdown(line.trim(), colors)}
            </p>
          );
        });
      } else {
        // Regular single paragraph
        result.push(
          <p 
            key={`para-${pIdx}`}
            style={{
              lineHeight: '1.85',
              fontSize: isMobile ? '1rem' : '1.1rem',
              margin: '1.25rem 0',
              color: colors.text,
              maxWidth: '100%'
            }}
          >
            {renderInlineMarkdown(block, colors)}
          </p>
        );
      }
    }
  });
  
  return result;
};

/**
 * Renders inline markdown (bold, etc.)
 */
const renderInlineMarkdown = (text, colors) => {
  const parts = [];
  let currentIndex = 0;
  const boldRegex = /\*\*(.+?)\*\*/g;
  let match;
  let lastIndex = 0;
  
  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before bold
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add bold text with accent color
    parts.push(
      <strong 
        key={`bold-${currentIndex}`}
        style={{ 
          fontWeight: '700', 
          color: colors.primary,
          letterSpacing: '-0.01em'
        }}
      >
        {match[1]}
      </strong>
    );
    
    lastIndex = match.index + match[0].length;
    currentIndex++;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};
